import { supabase } from './supabaseClient';

export interface HelpSection { question: string; answer: string; }
export interface ResidentHelpContent {
  scoreInfo: HelpSection[];
  privacyFaq: HelpSection[];
}

export const DEFAULT_SCORE_INFO: HelpSection[] = [
  {
    question: 'Where does this information come from?',
    answer: 'Your dashboard combines wellness measurements from the Care Daily system, the activities you attend in your community, the activities you log yourself, and your wellness goals. Most of it updates overnight each night. Your wellness mix uses the last 30 days, including today, and an activity you log shows up there within a few seconds.',
  },
  {
    question: 'What does my wellness mix show?',
    answer: 'The circle chart shows how your recent activity is spread across the areas of wellness your community tracks. A bigger slice means more of your recent activity is in that area. If an area says "No data yet", we simply don\'t have information for it — it doesn\'t mean you\'re doing poorly.',
  },
  {
    question: 'What do the colors mean?',
    answer: 'The charts and graphs on some tabs use colors when your community has set target ranges. Green means you\'re in the suggested range. Yellow means you\'re close. Red means there\'s room to improve.',
  },
  {
    question: 'What should I do with this?',
    answer: 'Use it to notice patterns and find activities you might enjoy. The suggested events are picked to help balance your wellness mix.',
  },
];

export const DEFAULT_PRIVACY_FAQ: HelpSection[] = [
  {
    question: 'Who can see my information?',
    answer: 'You, and the wellness team at your community. Your dashboard is not visible to other residents or to family members.',
  },
  {
    question: 'What information is collected?',
    answer: 'The community activities you attend, activities you choose to log yourself, and the goals you set here. The dashboard does not track your location, conversations, or anything outside these wellness activities.',
  },
  {
    question: 'How is my participation recorded?',
    answer: 'When you attend a community activity, staff record attendance. You can also log activities yourself on this dashboard.',
  },
  {
    question: 'How is my information protected?',
    answer: 'Your information is sent over secure, encrypted connections and stored in systems only authorized staff can access. It is used solely to support your wellness.',
  },
];

/** Compose the sections shown by the "What do these scores mean?" dialog for one page.
 *
 *  The dialog is opened from a page of metric cards, so it leads with the same
 *  explanation each card already carries behind its "i", then the facility-wide
 *  sections. Source precedence mirrors MetricCard's noteContent exactly: the
 *  admin-configured help text wins, and CareDaily's own metric comment fills in when
 *  that field is blank — which is the only description most metrics have.
 *  Pages where no metric has either text fall back to the facility sections alone,
 *  which is exactly the previous behaviour. */
export const buildScoreInfoSections = (
  metrics: Array<{
    metricKey: string;
    displayName: string;
    helpText?: string | null;
    /** CareDaily's metric comment, shown on the card when helpText is blank */
    fallbackText?: string | null;
    isVisible?: boolean;
  }>,
  facilitySections: HelpSection[]
): HelpSection[] => {
  const seenKeys = new Set<string>();
  const perMetric: HelpSection[] = [];

  for (const metric of metrics) {
    const answer = metric.helpText?.trim() || metric.fallbackText?.trim();
    if (metric.isVisible === false || !answer || seenKeys.has(metric.metricKey)) continue;
    seenKeys.add(metric.metricKey);
    perMetric.push({ question: `What does "${metric.displayName}" mean?`, answer });
  }

  return perMetric.length > 0 ? [...perMetric, ...facilitySections] : facilitySections;
};

const isValidSections = (v: unknown): v is HelpSection[] =>
  Array.isArray(v) && v.length > 0 &&
  v.every(s => s && typeof s.question === 'string' && typeof s.answer === 'string');

export class ResidentHelpContentService {
  static async getContent(facilityId: string): Promise<ResidentHelpContent> {
    try {
      const { data, error } = await supabase
        .from('resident_help_content')
        .select('score_info, privacy_faq')
        .eq('facility_id', facilityId)
        .maybeSingle();
      if (error || !data) {
        // Never fully silent: a read failure here can mask a deployment/RLS problem
        // while residents are shown generic defaults as if they were facility content.
        if (error) console.error('resident_help_content read failed; showing defaults:', error.message);
        return { scoreInfo: DEFAULT_SCORE_INFO, privacyFaq: DEFAULT_PRIVACY_FAQ };
      }
      return {
        scoreInfo: isValidSections(data.score_info) ? data.score_info : DEFAULT_SCORE_INFO,
        privacyFaq: isValidSections(data.privacy_faq) ? data.privacy_faq : DEFAULT_PRIVACY_FAQ,
      };
    } catch (e) {
      console.error('resident_help_content read threw; showing defaults:', e);
      return { scoreInfo: DEFAULT_SCORE_INFO, privacyFaq: DEFAULT_PRIVACY_FAQ };
    }
  }

  static async saveContent(
    facilityId: string,
    content: ResidentHelpContent,
    updatedBy: string
  ): Promise<void> {
    // Preserve the currently stored version for one-step rollback in the editor.
    // A read error here is indistinguishable in shape from "no row yet" ({data: null}),
    // so it must abort rather than fall through — otherwise a transient failure
    // silently overwrites the real prev_content with null on the next save.
    const { data: existing, error: readError } = await supabase
      .from('resident_help_content')
      .select('score_info, privacy_faq')
      .eq('facility_id', facilityId)
      .maybeSingle();
    if (readError) {
      throw new Error(`Failed to read prior help content before save: ${readError.message}`);
    }
    const { error } = await supabase
      .from('resident_help_content')
      .upsert(
        {
          facility_id: facilityId,
          score_info: content.scoreInfo,
          privacy_faq: content.privacyFaq,
          prev_content: existing ?? null,
          updated_by: updatedBy,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'facility_id' }
      );
    if (error) throw new Error(`Failed to save help content: ${error.message}`);
  }

  static async getPreviousContent(facilityId: string): Promise<ResidentHelpContent | null> {
    const { data } = await supabase
      .from('resident_help_content')
      .select('prev_content')
      .eq('facility_id', facilityId)
      .maybeSingle();
    const prev = data?.prev_content;
    if (!prev) return null;
    return {
      scoreInfo: isValidSections(prev.score_info) ? prev.score_info : [],
      privacyFaq: isValidSections(prev.privacy_faq) ? prev.privacy_faq : [],
    };
  }
}
